import { Backdrop } from '@material-ui/core';
import { attributes, Linked } from '@type-r/models';
import { useIO, useLinked, useModel } from '@type-r/react';
import { fetchJson } from 'api/endpoint';
import { Button, CollapsableSection, Column, Columns, CompactTextArea, FormCheckbox, Loading, PrimaryButton, ReadOnly } from 'lqx-react';
import * as React from 'react';
import { UserMini, Tuple, Permissions, Roles } from './api';


const State = attributes({
    userRoles: [Tuple],
    userPermissions: [Tuple],
    selectedPermissions: Array,
    selectedRelationshipIds: Array,
    selectedDeletedRelationshipIds: Array,
    savingPermission: Boolean,
    deletingPermission: Boolean,
    savingRole: Boolean
})

export const EditPermissions = ({ user }: { user: UserMini }) => {

    const state = useModel(State);

    const loadData = async (user) => {
        const results = await Promise.all([getUserRole(user.email), getUserPermissions(user.email)])
        state.userRoles.set(results[0], { parse: true })
        state.userPermissions.set(results[1], { parse: true })

        state.selectedPermissions = (state.userPermissions.map(x => x.item1.split(',')) as any).flat().map(x => x.trim())
        state.selectedRelationshipIds = (state.userPermissions.map(x => x.item2) as any).flat()
    }

    let isReady = useIO(async () => await loadData(user), [user]);

    isReady = useIO(async () => {
        if (state.savingPermission) {
            try {
                await savePermissions(user.email, state.selectedPermissions, state.selectedRelationshipIds)
            } finally {
                state.savingPermission = false
                await loadData(user)
            }
        }
    }, [state.savingPermission]) && isReady

    isReady = useIO(async () => {
        if (state.deletingPermission) {
            try {
                await deletePermissions(user.email, state.selectedPermissions, state.selectedDeletedRelationshipIds)
            } finally {
                state.deletingPermission = false
                await loadData(user)
            }
        }
    }, [state.deletingPermission]) && isReady

    return (
        isReady ?
            <>
                <CollapsableSection title="Roles">
                    <>{Roles.map(role =>
                        <EditRoleRow key={role} role={role} userRoles={state.userRoles} email={user.email} />
                    )}</>
                </CollapsableSection>
                <CollapsableSection title="Permissions">
                    <ReadOnly.Provider value={state.savingPermission}>
                        <Columns>
                            <Column>
                                {
                                    Permissions.map(permission =>
                                        <div key={permission}>
                                            <FormCheckbox $checked={state.$.selectedPermissions.contains(permission)} />{permission}
                                        </div>
                                    )
                                }
                            </Column>
                            <Column>
                                <CompactTextArea label="RelationshipIds" rows={10} $value={Linked.value(state.selectedRelationshipIds.join("\n"),
                                    (x) => { state.selectedRelationshipIds = x.split("\n") })} />
                                <PrimaryButton onClick={() => state.savingPermission = true} disabled={state.savingPermission}>Save</PrimaryButton>

                                <CompactTextArea label="RelationshipIds to remove from selected permissions" rows={10} $value={Linked.value(state.selectedDeletedRelationshipIds.join("\n"),
                                    (x) => { state.selectedDeletedRelationshipIds = x.split("\n") })} />
                                <Button onClick={() => state.deletingPermission = true} disabled={state.deletingPermission}>Delete</Button>

                            </Column>
                        </Columns>
                    </ReadOnly.Provider>
                </CollapsableSection>

            </>
            : <Loading />
    )
};

const EditRoleRow = ({ role, userRoles, email }) => {

    const $doingIO = useLinked(null);

    const userRole = userRoles.get(role)
    const checked = userRole != undefined
    const $checked = Linked.value(checked, (x) => { x ? userRoles.add({ item1: role }) : userRoles.remove({ item1: role }) })

    let isReady = useIO(async () => {
        if ($doingIO.value == "Add") {
            try {
                await saveRole(email, userRole, false)
            } finally {
                $doingIO.set(null)
            }

        } else if ($doingIO.value == "Reset") {

            try {
                await saveRole(email, userRole, true)
            } finally {
                $doingIO.set(null)
            }
        }
    }, [$doingIO.value])

    return (
        <div key={role.cid}>
            <FormCheckbox $checked={$checked} />{role}

            {checked ?
                <>
                    <CompactTextArea label="Companies" rows={10} $value={Linked.value(userRole.item2.join("\n"), (x) => { userRole.item2 = x.split("\n") })} />
                    <Columns>
                        <Column>
                            <PrimaryButton onClick={() => {
                                $doingIO.set("Add")
                            }}>Add</PrimaryButton>
                        </Column>
                        <Column>
                            <Button onClick={() => {
                                $doingIO.set("Reset")
                            }}>Reset To</Button>
                        </Column>
                    </Columns>

                </>
                : null
            }
            {!isReady ?
                <Backdrop open={true} style={{ color: '#fff', zIndex: 1000 }}
                >
                    <Loading />
                </Backdrop>
                : null}
        </div>)
}

async function saveRole(email, userRole, isReset) {
    return await fetchJson(isReset ? 'POST' : 'PUT', `/api/inblock/tradetalk/v1/user/role`, {
        "userEmails": [
            email
        ],
        "role": userRole.item1,
        "companyNames": userRole.item2
    })
}

async function savePermissions(email, permissions, relationshipids) {
    return await fetchJson('POST', `/api/inblock/tradetalk/v1/user/permissions`, {
        "userEmails": [
            email
        ],
        "relationshipIds": relationshipids,
        "permissions": permissions.filter(x => x != "")
    })
}

async function deletePermissions(email, permissions, relationshipids) {
    return await fetchJson('DELETE', `/api/inblock/tradetalk/v1/user/relationships`, {
        "userEmails": [
            email
        ],
        "relationshipIds": relationshipids,
        "permissions": permissions.filter(x => x != "")
    })
}

async function getUserRole(email) {
    return await fetchJson('GET', `/api/inblock/tradetalk/v1/user/${email}/role`);
}

async function getUserPermissions(email) {
    return await fetchJson('GET', `/api/inblock/tradetalk/v1/user/${email}/permissions`);
}